import React, { useState } from "react";
import {
  Box,
  Button,
  LinearProgress,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import CustomModal from "./CustomModal";
import { DynamicIcon } from "../utilities/iconUtils";
import Search from "./Search";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { createHeaders, createRows } from "../methods";
import { GetDistinctColumnValues } from "../services";

const ICD10CodesModal = ({ openModal, setOpenModal, metadata }) => {
  const [page, setPage] = useState(1);
  const [data, setData] = useState({
    rows: [],
  });
  const [headers, setHeaders] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [nextData, setNextData] = useState([]);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const [searchValue, setSearchValue] = useState('');

  const getDistinctColumnValues = async (
    searchText = "",
    usePaging = false,
    page,
    filterType = "contains"
  ) => {
    if (openModal.icd10) {
      const jsonBody = metadata.getDistinctIcd10Codes(searchText);
      jsonBody.page = usePaging ? page : 1;
      jsonBody.filterType = filterType;
      jsonBody.db = metadata.db;
      const body = jsonBody;
      let resData = [];
      let count = 0;

      setLoading(true);

      await GetDistinctColumnValues({ body })
        .then((res) => {
          if (res && res.success === true) {
            const apiRes = JSON.parse(res.data);
            count = apiRes.rowCount;
            resData = JSON.parse(res.data).response;

            setLoading(false);

            const createdRows = createRows(resData, page);
            const tableHeaders = createHeaders(resData);

            if (searchText !== "") {
              setHeaders(tableHeaders);
              setData({ rows: createdRows });
              setCount(count);
            } else {
              if (page === 1) {
                setHeaders(tableHeaders);
                setData({ rows: createdRows });
                setCount(count);
              }
              if (resData?.length > 0 && usePaging && page !== 1) {
                setNextData([...nextData, ...createdRows]);
              }
            }

            if (count > 100) {
              setPage((prevState) => prevState + 1);
            }
          }
        })
        .catch((err) => {
          console.log(err);
          setLoading(false);
        });
    }
  };

  const handleOnRowsScrollEnd = () => {
    if (data?.rows.length < 1) {
      setPage(1);
      getDistinctColumnValues("", true, 1);
    } else if (
      openModal.icd10 === true &&
      data?.rows.length + nextData.length !== count
    ) {
      setPage((prevState) => prevState++);
      getDistinctColumnValues("", true, page);
    }
  };

  const handleCloseModal = () => {
    setOpenModal({ ...openModal, icd10: false });
  };

  const modalActions = (
    <Button
      sx={{
        backgroundColor: theme.palette.grey[500],
        color: 'white',
        '&:hover': {
          backgroundColor: theme.palette.grey[600],
        },
      }}
      onClick={() => handleCloseModal()}
    >
      Close
    </Button>
  );

  return (
    <CustomModal
      open={openModal.icd10}
      onClose={handleCloseModal}
      title="ICD 10 Codes Lookup"
      subtitle="Search and browse ICD-10 diagnostic codes"
      icon={
        <DynamicIcon
          name="FileText"
          size={20}
          style={{ color: 'white' }}
        />
      }
      maxWidth="lg"
      actions={modalActions}
      contentPadding={true}
    >
      <Box>
        <Box sx={{ mb: theme.spacing(1) }}>
          <Search
            getDistinctColumnValues={getDistinctColumnValues}
            setPage={setPage}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
          />
        </Box>

        <Box sx={{ height: 500, width: '100%' }}>
          {data?.rows && (
            <DataGridPro
              rows={JSON.parse(JSON.stringify(data?.rows.concat(nextData)).replaceAll('[char(10)]', '').replaceAll('[char(13)]', '').replaceAll(/\[Ident\d+\]/gmi, ''))}
              columns={headers}
              loading={loading}
              rowCount={count}
              hideFooterPagination
              onRowsScrollEnd={() => handleOnRowsScrollEnd()}
              slots={{
                loadingOverlay: LinearProgress,
              }}
              sx={{
                border: '1px solid',
                borderColor: theme.palette.mode === 'dark'
                  ? theme.palette.grey[700]
                  : theme.palette.grey[300],
                borderRadius: '12px',
                overflow: 'hidden',
                backgroundColor: theme.palette.background.paper,
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 4px 6px -1px rgb(0 0 0 / 0.15), 0 2px 4px -2px rgb(0 0 0 / 0.15)'
                  : '0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05)',

                // Header styling
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? theme.palette.grey[800]
                    : theme.palette.grey[50],
                  borderBottom: '1px solid',
                  borderBottomColor: theme.palette.mode === 'dark'
                    ? theme.palette.grey[700]
                    : theme.palette.grey[200],
                  minHeight: '48px !important',
                  maxHeight: '48px !important',
                  '& .MuiDataGrid-columnHeader': {
                    '&:focus': {
                      outline: 'none',
                    },
                    '&:focus-within': {
                      outline: 'none',
                    },
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    color: theme.palette.text.primary,
                    letterSpacing: '0.025em',
                  },
                },

                // Column separators
                '& .MuiDataGrid-columnSeparator': {
                  display: 'flex',
                  color: theme.palette.mode === 'dark'
                    ? theme.palette.grey[600]
                    : theme.palette.grey[300],
                },

                // Row styling
                '& .MuiDataGrid-row': {
                  borderBottom: '1px solid',
                  borderBottomColor: theme.palette.mode === 'dark'
                    ? theme.palette.grey[800]
                    : theme.palette.grey[100],
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.04)'
                      : 'rgba(0, 0, 0, 0.02)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? `${theme.palette.primary.main}20`
                      : `${theme.palette.primary.main}08`,
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark'
                        ? `${theme.palette.primary.main}30`
                        : `${theme.palette.primary.main}12`,
                    },
                  },
                },

                // Cell styling
                '& .MuiDataGrid-cell': {
                  borderRight: '1px solid',
                  borderRightColor: theme.palette.mode === 'dark'
                    ? theme.palette.grey[800]
                    : theme.palette.grey[100],
                  padding: '12px 16px',
                  fontSize: '0.875rem',
                  lineHeight: '1.25rem',
                  color: theme.palette.text.primary,
                  display: 'flex',
                  alignItems: 'center',
                  '&:focus': {
                    outline: 'none',
                  },
                  '&:focus-within': {
                    outline: 'none',
                  },
                  '&:last-child': {
                    borderRight: 'none',
                  },
                },

                // Checkbox styling
                '& .MuiDataGrid-checkboxInput': {
                  color: theme.palette.primary.main,
                  '&.Mui-checked': {
                    color: theme.palette.primary.main,
                  },
                  '& .MuiSvgIcon-root': {
                    fontSize: '1.25rem',
                    borderRadius: '4px',
                  },
                },

                // Virtualization container
                '& .MuiDataGrid-virtualScroller': {
                  backgroundColor: theme.palette.background.paper,
                },

                '& .MuiDataGrid-main': {
                  '&:focus': {
                    outline: 'none',
                  },
                },

                // Footer styling 
                '& .MuiDataGrid-footerContainer': {
                  borderTop: '1px solid',
                  borderTopColor: theme.palette.mode === 'dark'
                    ? theme.palette.grey[700]
                    : theme.palette.grey[200],
                  backgroundColor: theme.palette.mode === 'dark'
                    ? theme.palette.grey[900]
                    : theme.palette.grey[50],
                },

                // Scrollbar styling for webkit browsers
                '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
                  width: '8px',
                  height: '8px',
                },
                '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? theme.palette.grey[800]
                    : theme.palette.grey[100],
                  borderRadius: '4px',
                },
                '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? theme.palette.grey[600]
                    : theme.palette.grey[400],
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? theme.palette.grey[500]
                      : theme.palette.grey[500],
                  },
                },
              }}
            />
          )}
        </Box>
      </Box>
    </CustomModal>
  );
};

export default ICD10CodesModal;
